// Location: src/components/admin/analytics/AnalyticsIndex.tsx
// Purpose: Main analytics dashboard navigation with beautiful gradient cards
// Scope: Routes to individual analytics sections for each game

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
	Wallet,
	Target,
	Trophy,
	Zap,
	Settings,
	TrendingUp,
	Users,
	Eye,
	Heart,
	MessageCircle,
	ArrowRight,
} from 'lucide-react';

interface AnalyticsCardProps {
	title: string;
	description: string;
	route: string;
	gradient: string;
	icon: React.ReactNode;
	stats?: {
		label: string;
		value: string | number;
		trend?: string;
	}[];
	isNew?: boolean;
	requiresSuperAdmin?: boolean;
	defaultImage: string;
}

function AnalyticsCard({
	title,
	description,
	route,
	gradient,
	icon,
	stats,
	isNew,
	requiresSuperAdmin,
	defaultImage,
}: AnalyticsCardProps) {
	const navigate = useNavigate();
	const { isSuperAdmin } = useAuth();

	// Don't show super admin cards to non-super admins
	if (requiresSuperAdmin && !isSuperAdmin) {
		return null;
	}

	return (
		<Card
			className={cn(
				'relative overflow-hidden cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl group',
				gradient
			)}
			onClick={() => navigate(route)}
		>
			{/* Background Image with Overlay */}
			<div className='absolute inset-0 opacity-10 group-hover:opacity-20 transition-opacity duration-300'>
				<img
					src={defaultImage}
					alt=''
					className='w-full h-full object-cover'
				/>
			</div>

			{/* Gradient Overlay */}
			<div
				className={cn(
					'absolute inset-0 opacity-50 group-hover:opacity-10 transition-opacity duration-300',
					gradient
						.replace('from-', 'bg-gradient-to-br from-')
						.replace('to-', 'to-')
				)}
			/>

			<CardHeader className='relative z-10'>
				<div className='flex items-start justify-between'>
					<div className='flex items-center gap-3'>
						<div className='p-3 bg-white/20 backdrop-blur-sm rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300'>
							{icon}
						</div>
						<div>
							<CardTitle className='text-white text-xl font-bold flex items-center gap-2'>
								{title}
								{isNew && (
									<Badge className='bg-yellow-400 text-black border-0 animate-pulse'>
										NEW
									</Badge>
								)}
							</CardTitle>
							<p className='text-white/80 text-sm mt-1'>
								{description}
							</p>
						</div>
					</div>
					<ArrowRight className='h-5 w-5 text-white/60 group-hover:translate-x-1 transition-transform duration-300' />
				</div>
			</CardHeader>

			<CardContent className='relative z-10'>
				{stats && stats.length > 0 && (
					<div className='grid grid-cols-3 gap-4 mt-4'>
						{stats.map((stat, index) => (
							<div key={index} className='text-center'>
								<div className='text-2xl font-bold text-white'>
									{stat.value}
								</div>
								<div className='text-xs text-white/70 mt-1'>
									{stat.label}
								</div>
								{stat.trend && (
									<div className='text-xs text-green-300 mt-1 flex items-center justify-center gap-1'>
										<TrendingUp className='h-3 w-3' />
										{stat.trend}
									</div>
								)}
							</div>
						))}
					</div>
				)}

				{/* Hover Effect - View Analytics Text */}
				<div className='mt-4 pt-4 border-t border-white/20 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity duration-300'>
					<span className='text-white/90 text-sm font-medium'>
						View Analytics
					</span>
					<ArrowRight className='h-4 w-4 text-white/90' />
				</div>
			</CardContent>
		</Card>
	);
}

export function AnalyticsIndex() {
	const { user, isAdmin } = useAuth();
	const defaultImage =
		'https://watchers.sirv.com/watchers/covers/purple-logo%3AUsers%3Athereverend%3ADropbox%3AWeb%20Projects%3AWatcher%20Society%3Aassets%3Alogo%3Apurple-logo-flames.gif-flames.gif';

	if (!user || !isAdmin) {
		return (
			<div className='container mx-auto p-6'>
				<Card className='mt-6'>
					<CardContent>
						<div className='flex flex-col items-center justify-center space-y-6 p-8'>
							<h2 className='text-2xl font-bold text-red-500'>
								Access Denied
							</h2>
							<p className='text-lg text-gray-400 text-center'>
								You do not have permission to access Analytics.
							</p>
						</div>
					</CardContent>
				</Card>
			</div>
		);
	}

	const analyticsCards: AnalyticsCardProps[] = [
		{
			title: 'KARMA Social Rewards',
			description:
				'Track social tipping, rewards, and KARMA distribution',
			route: '/admin/analytics/karma-social',
			gradient: 'from-emerald-500 to-green-600',
			icon: <Wallet className='h-6 w-6 text-white' />,
			stats: [
				{ label: 'Total KARMA', value: '45.2K', trend: '+12%' },
				{ label: 'Active Users', value: '1,234', trend: '+8%' },
				{ label: 'Daily Tips', value: '89', trend: '+25%' },
			],
			defaultImage,
		},
		{
			title: 'Affiliate Rewards',
			description:
				'Monitor affiliate performance, conversions, and earnings',
			route: '/admin/analytics/affiliate',
			gradient: 'from-pink-500 to-purple-600',
			icon: <Target className='h-6 w-6 text-white' />,
			stats: [
				{ label: 'Total Leads', value: '892', trend: '+15%' },
				{ label: 'Sales', value: '234', trend: '+10%' },
				{ label: 'Conv. Rate', value: '26.2%', trend: '+2%' },
			],
			defaultImage,
		},
		{
			title: 'Social Profile Game',
			description:
				'View profile game leaderboards and performance metrics',
			route: '/admin/analytics/social-game',
			gradient: 'from-blue-500 to-indigo-600',
			icon: <Trophy className='h-6 w-6 text-white' />,
			stats: [
				{ label: 'Players', value: '567', trend: '+18%' },
				{ label: 'Avg Score', value: '72.3', trend: '+5%' },
				{ label: 'Top Tier', value: 'Diamond' },
			],
			defaultImage,
		},
		{
			title: 'Buzz Agent Game',
			description: 'Track Buzz Agent performance and engagement metrics',
			route: '/admin/analytics/buzz-agents',
			gradient: 'from-purple-500 to-pink-600',
			icon: <Zap className='h-6 w-6 text-white' />,
			stats: [
				{ label: 'Active Agents', value: '89', trend: '+22%' },
				{ label: 'Feed Items', value: '12.3K', trend: '+30%' },
				{ label: 'Engagement', value: '8.9K', trend: '+15%' },
			],
			isNew: true,
			defaultImage,
		},
		{
			title: 'Game Settings',
			description: 'See game settings, status, and reward distributions.',
			route: '/admin/analytics/lifecycle',
			gradient: 'from-yellow-500 to-orange-600',
			icon: <Settings className='h-6 w-6 text-white' />,
			stats: [
				{ label: 'Games Active', value: '2/4' },
				{ label: 'Season', value: 'S1' },
				{ label: 'Days Left', value: '14' },
			],
			requiresSuperAdmin: false, // Allow all admins to view
			defaultImage,
		},
	];

	return (
		<div className='space-y-6 my-20'>
			{/* Header */}
			<div className='flex items-center justify-between'>
				<div>
					<h1 className='text-3xl font-bold text-white'>
						Analytics Dashboard
					</h1>
					<p className='text-muted-foreground mt-2'>
						Comprehensive tracking and analytics for all games and
						systems
					</p>
				</div>
				<div className='flex items-center gap-2'>
					<Badge
						variant='outline'
						className='bg-gradient-to-r from-blue-500 to-purple-500 text-white border-0'
					>
						<Eye className='h-3 w-3 mr-1' />
						Real-time Data
					</Badge>
				</div>
			</div>

			{/* Stats Overview */}
			<div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
				<Card className='bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/20'>
					<CardContent className='p-4'>
						<div className='flex items-center justify-between'>
							<div>
								<p className='text-sm text-muted-foreground'>
									Total Views
								</p>
								<p className='text-2xl font-bold text-green-500'>
									234.5K
								</p>
							</div>
							<Eye className='h-8 w-8 text-green-500/30' />
						</div>
					</CardContent>
				</Card>

				<Card className='bg-gradient-to-br from-blue-500/10 to-indigo-500/10 border-blue-500/20'>
					<CardContent className='p-4'>
						<div className='flex items-center justify-between'>
							<div>
								<p className='text-sm text-muted-foreground'>
									Total Users
								</p>
								<p className='text-2xl font-bold text-blue-500'>
									1,892
								</p>
							</div>
							<Users className='h-8 w-8 text-blue-500/30' />
						</div>
					</CardContent>
				</Card>

				<Card className='bg-gradient-to-br from-pink-500/10 to-purple-500/10 border-pink-500/20'>
					<CardContent className='p-4'>
						<div className='flex items-center justify-between'>
							<div>
								<p className='text-sm text-muted-foreground'>
									Engagement
								</p>
								<p className='text-2xl font-bold text-pink-500'>
									89.2K
								</p>
							</div>
							<Heart className='h-8 w-8 text-pink-500/30' />
						</div>
					</CardContent>
				</Card>

				<Card className='bg-gradient-to-br from-purple-500/10 to-indigo-500/10 border-purple-500/20'>
					<CardContent className='p-4'>
						<div className='flex items-center justify-between'>
							<div>
								<p className='text-sm text-muted-foreground'>
									Comments
								</p>
								<p className='text-2xl font-bold text-purple-500'>
									45.7K
								</p>
							</div>
							<MessageCircle className='h-8 w-8 text-purple-500/30' />
						</div>
					</CardContent>
				</Card>
			</div>

			{/* Analytics Cards Grid */}
			<div className='grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6'>
				{analyticsCards.map((card, index) => (
					<AnalyticsCard key={index} {...card} />
				))}
			</div>

			{/* Footer Note */}
			<div className='text-center text-sm text-muted-foreground mt-8'>
				<p>
					All metrics are updated in real-time. Click any card to view
					detailed analytics.
				</p>
			</div>
		</div>
	);
}
